/**
 * pricing.js — Zentrale Pricing-Konfiguration
 *
 * Einzige Quelle der Wahrheit für Preispläne, Add-ons, FAQ und
 * Zielgruppen-Empfehlungen im KI Social Media Manager (Förderfuchs).
 *
 * Kein Billing / kein Stripe — nur Datenkonfiguration.
 * Alle Preise in Euro (€), monatlich und jährlich.
 *
 * ── 5-Stufen-Paketlogik (Owner-Direktive 2026-07-19) ──────────────────────────
 *   1. Testphase   → trialConfig (14 Tage, nur Bluesky, keine Karte)
 *   2. Starter     →  99 € · 1 Kanal · 1 Beitrag/Tag
 *   3. Wachstum    → 279 € · 3 Kanäle · 1 Beitrag/Tag je Kanal · EMPFOHLEN
 *   4. Dominanz    → 699 € · alle verfügbaren Kanäle · 3 Beiträge/Tag je Kanal
 *   5. Enterprise  → individuelle Anfrage (kein Festpreis, kein Checkout)
 *
 * Dominanz-Preisherleitung (2026-07-19, Markt-Analyse):
 *   Leiter 99 → 279 → 699 = fallende Multiplikatoren (2,8× → 2,5×), liest sich
 *   als faire Mengendegression. 699 € liegt bewusst ÜBER der Self-Service-
 *   SaaS-Decke (~400 €, SocialBee/Hootsuite/Sprout-Topfpläne) → wird als
 *   Agentur-Ersatz gelesen, nicht als Tool; unter der 700-€-Schwelle, bis zu
 *   der KMU ohne formale Budgetfreigabe selbst buchen; 80–90 % unter dem
 *   Agentur-Äquivalent für täglichen Multi-Kanal-Output (3.500–6.000 €+/Monat).
 *
 * Exportierte Daten:
 *   trialConfig            → Testphasen-Konfiguration (14 Tage, Standard) + Trial-Karte
 *   pricingPlans           → Array der Hauptpakete (Solo[hidden], Starter, Wachstum, Dominanz, Enterprise)
 *   pricingAddons          → Array der Add-ons
 *   pricingFaq             → Array der FAQ-Einträge
 *   audienceRecommendations → Zielgruppen-Mapping → Plan-Empfehlung
 *
 * Exportierte Helper:
 *   recommendPlanForAudience(audienceKey)  → Empfehlung für Zielgruppe
 *   getPlanById(planId)                    → Plan per ID
 *   getAddonById(addonId)                  → Add-on per ID
 */

// ── Jahresrabatt-Konfiguration ────────────────────────────────────────────────
//
// Zentrale Quelle für den Jahresrabatt-Hinweis.
// Alle UI-Texte (Badge im Toggle, FAQ-Antwort) sollen hieraus abgeleitet werden.
// Basiert auf den tatsächlichen priceMonthly / priceYearly-Werten der Pläne (~15 %).

export const yearlyDiscountInfo = {
  percentageApprox: 15,
  label: "bis zu 15 % sparen",
  faqNote:
    "Bei jährlicher Zahlung sparst du ca. 15 % im Vergleich zur monatlichen Abrechnung.",
};

// ── Testphasen-Konfiguration (Stufe 1 der Paketlogik) ─────────────────────────
//
// Einzige Quelle der Wahrheit für die kostenlose Testphase.
// Genau ein Modell: 14 Tage Standard-Trial ohne Zahlungsmittel-Pflicht.
// Alle UI-Texte und Hook-Beschreibungen sollen diesen Wert referenzieren,
// niemals eine abweichende Zahl hardcoden.

export const trialConfig = {
  enabled: true,
  days: 14,
  type: "standard",
  requiresPaymentMethod: false,
  shortLabel: "14 Tage kostenlos testen",
  // Keine Kreditkarte → keine automatische Abbuchung. Bewusst KEIN „danach automatisch",
  // damit kein Abofallen-Verdacht entsteht (DACH-Käufer reagieren darauf sensibel).
  disclaimer: "Nach Ablauf der Testphase wählst du aktiv deinen Plan — keine automatische Abbuchung.",
  // Kanal-Regel der Testphase (Owner-Direktive 2026-07-06, serverseitig durchgesetzt):
  // alle Funktionen frei, veröffentlicht wird über Bluesky. Weitere Kanäle = nur für
  // gekaufte Pakete. Muss zu backend/billing/plans.py → TRIAL_CHANNELS passen.
  trialChannels: ["bluesky"],
  // Befund 19.08.2026 (§5 UWG): Hier stand „alle weiteren Kanäle schaltest du mit
  // deinem Paket frei". Mit einem gekauften Paket kommt heute genau EIN weiterer
  // Kanal dazu (LinkedIn); Facebook, Instagram, Threads, TikTok und das Google
  // Unternehmensprofil hängen an Freigaben der Anbieter, nicht am Paket.
  channelNote: "In der Testphase veröffentlichst du über Bluesky. Mit einem Paket kommt LinkedIn dazu; weitere Kanäle schalten wir frei, sobald der jeweilige Anbieter sie freigibt — ohne Aufpreis.",
  // ── Trial-Karte (Stufe 1 auf der Pricing-Seite) ────────────────────────────
  // Wird als eigene Einstiegs-Karte VOR den Bezahlplänen gerendert — bewusst
  // NICHT Teil von pricingPlans (kein Checkout, keine Feature-Gates, keine
  // Störung von getRequiredPlanForFeature / pricingPlans[0]-Fallbacks).
  card: {
    name: "Testphase",
    badge: "Kostenlos testen",
    promise: "Erlebe den Autopiloten 14 Tage — ohne Karte, ohne Risiko",
    description:
      "Lerne alle Abläufe in Ruhe kennen: Markenprofil, Monatsplan, fertige Beiträge. Veröffentlicht wird über Bluesky.",
    features: [
      "14 Tage alle Funktionen frei",
      "Veröffentlichen über Bluesky",
      "Website-Analyse & Markenprofil",
      "Keine Kreditkarte nötig",
    ],
    cta: "Kostenlos testen",
  },
};

// ── Hauptpakete (Stufen 2–5) ──────────────────────────────────────────────────
//
// limits.postsPerDayPerChannel = verkaufte Tages-Schlagzahl je gebuchtem Kanal
// (Anzeige-/Versprechens-Wert; serverseitig gilt weiterhin postsPerMonth als
// Platzierungs-Quota, siehe backend/billing/plans.py).
// requestOnly: true → Plan hat keinen Checkout; CTA führt in den Kontakt-Flow
// (usePlanSelection verzweigt darauf, Backend blockt den Tier im Checkout).

export const pricingPlans = [
  {
    id: "solo",
    hidden: true,   // Legacy-Plan: aus der Preisführung ausgeblendet, bleibt Gate-Baseline
                    // (useFeatureGate DEFAULT_PLAN_ID="solo") und Stripe-Bestandskunden-Tier.
    name: "Solo",
    priceMonthly: 49,
    priceYearly: 42,
    badge: null,
    highlighted: false,
    promise: "≈ 5 fertige Beiträge pro Woche — regelmäßig sichtbar ohne eigenen Aufwand",
    description: "Für Selbstständige und lokale Dienstleister, die regelmäßig präsent sein wollen.",
    idealFor: ["1 Marke", "Bis 3 Kanäle", "Selbstständige", "Lokale Dienstleister"],
    features: [
      "Bis zu 20 fertige Beiträge pro Monat",
      "1 Brand / Markenprofil",
      "Bis zu 3 Social-Media-Kanäle",
      "Website-Analyse & Markenprofil",
      "KI-Postideen, Captions & Hashtags",
      "Bild- & Thumbnail-Tipps",
      "Einfacher Assistent (Wizard)",
      "Monatsplan & Kalender",
    ],
    limits: {
      brands: 1,
      channels: 3,
      postsPerMonth: 20,
      postsPerDayPerChannel: null,
    },
    // ── Feature-Gates: welche App-Bereiche für diesen Plan freigeschaltet sind ──
    // false = nicht im Plan enthalten (Nudge im UI), true = verfügbar
    featureGates: {
      link_in_bio:        true,   // Link-in-Bio — günstiges Starter-Feature (alle Pläne)
      website_insights:   true,   // Website-Analyse & Markenprofil inklusive
      simple_assistant:   true,   // Einfacher Wizard inklusive
      bonus_center:       true,   // Bonus & Empfehlungscenter inklusive (alle Pläne)
      masterpost:         false,  // ab Starter
      content_tools:      false,  // ab Starter
      strategy:           false,  // ab Starter
      profile_optimizer:  false,  // ab Starter
      social_inbox:       false,  // ab Starter
      advanced_analytics: false,  // ab Starter
      evergreen:          false,  // ab Wachstum
      campaigns:          false,  // ab Wachstum
      media_library:      false,  // ab Wachstum
      multi_brand:        false,  // ab Dominanz
      advanced_formats:   false,  // ab Wachstum (Format-Module)
      team_access:        false,  // ab Enterprise
      client_workspaces:  false,  // ab Enterprise
    },
    cta: "Mit Solo starten",
    ctaVariant: "outline",
  },
  {
    id: "business",
    name: "Starter",
    priceMonthly: 99,
    priceYearly: 84,
    badge: null,
    highlighted: false,
    // Stufe 2: der reibungsarme Einstieg in den Autopiloten.
    promise: "1 Kanal, jeden Tag ein fertiger Beitrag — automatisch",
    description:
      "Einfach starten: Dein wichtigster Kanal wird täglich bespielt — geplant, geschrieben und veröffentlicht vom Autopiloten.",
    idealFor: ["Solo-Selbstständige", "Kleine Unternehmen", "Lokale Anbieter"],
    features: [
      "1 Social-Media-Kanal deiner Wahl",
      "1 Beitrag pro Tag — automatisch erstellt & veröffentlicht",
      "KI-Texte, Hashtags & Bildideen aus deinem Markenprofil",
      "Strategie, Analytics & Social Inbox",
      "Monatsplan & Kalender",
    ],
    limits: {
      brands: 1,
      channels: 1,
      postsPerMonth: 31,
      postsPerDayPerChannel: 1,
    },
    featureGates: {
      link_in_bio:        true,   // Link-in-Bio — günstiges Starter-Feature (alle Pläne)
      website_insights:   true,
      simple_assistant:   true,
      bonus_center:       true,   // Bonus & Empfehlungscenter inklusive (alle Pläne)
      masterpost:         true,   // Masterpost & Multi-Channel-Posting inklusive
      content_tools:      true,   // Content Tools (Pain-Point-Analyse) inklusive
      strategy:           true,   // Strategie-Experte & Planung inklusive
      profile_optimizer:  true,
      social_inbox:       true,
      advanced_analytics: true,   // Erweiterte Analytics inklusive
      evergreen:          false,  // ab Wachstum
      campaigns:          false,  // ab Wachstum
      media_library:      false,  // ab Wachstum
      multi_brand:        false,  // ab Dominanz
      advanced_formats:   false,  // ab Wachstum
      team_access:        false,  // ab Enterprise
      client_workspaces:  false,  // ab Enterprise
    },
    cta: "Mit Starter loslegen",
    ctaVariant: "primary",
  },
  {
    id: "creator",
    name: "Wachstum",
    priceMonthly: 279,
    priceYearly: 237,
    badge: "Empfohlen",
    highlighted: true,
    // Stufe 3: der empfohlene Kernplan — 3 Kanäle, täglich je 1 Beitrag.
    promise: "3 Kanäle, jeden Tag frisch bespielt — dein Social Media läuft",
    description:
      "Der sinnvolle Standard: drei Kanäle, täglich je ein Beitrag — der Autopilot plant, schreibt und veröffentlicht für dich.",
    idealFor: ["Wachsende Unternehmen", "Regelmäßiger Content", "3 Kanäle täglich"],
    features: [
      "3 Social-Media-Kanäle",
      "1 Beitrag pro Tag je Kanal — bis zu 90 Veröffentlichungen im Monat",
      "Alles aus Starter",
      "Automatischer Monatsplan",
      "Evergreen-Content & Kampagnen",
      "Prioritäts-Support",
    ],
    limits: {
      brands: 1,
      channels: 3,
      postsPerMonth: 93,
      postsPerDayPerChannel: 1,
    },
    featureGates: {
      link_in_bio:        true,   // Link-in-Bio — günstiges Starter-Feature (alle Pläne)
      website_insights:   true,
      simple_assistant:   true,
      bonus_center:       true,   // Bonus & Empfehlungscenter inklusive (alle Pläne)
      masterpost:         true,
      content_tools:      true,
      strategy:           true,
      profile_optimizer:  true,
      social_inbox:       true,
      advanced_analytics: true,
      evergreen:          true,   // Evergreen Content-System inklusive
      campaigns:          true,   // Kampagnen-Management inklusive
      media_library:      true,   // Medien-Bibliothek inklusive
      multi_brand:        false,  // 1 Marke — Mehrmarken ab Dominanz oder per Add-on
      advanced_formats:   true,   // Zugang zu Format-Modulen (Karussell, Video)
      team_access:        false,  // ab Enterprise
      client_workspaces:  false,  // ab Enterprise
    },
    cta: "Wachstum starten",
    ctaVariant: "outline",
  },
  {
    id: "scale",
    name: "Dominanz",
    priceMonthly: 699,
    priceYearly: 594,
    badge: "Für Power-User",
    highlighted: false,
    // Stufe 4: maximale Präsenz und Schlagzahl — Preisherleitung siehe Kopf-Kommentar.
    promise: "Alle verfügbaren Kanäle, dreimal täglich bespielt — Präsenz wie eine Agentur",
    description:
      "Für Marken, die überall gleichzeitig sichtbar sein wollen: Der Autopilot bespielt jeden verfügbaren Kanal dreimal täglich — voll automatisch.",
    idealFor: ["Starke Marken", "Hoher Output-Bedarf", "Agentur-Ersatz"],
    features: [
      "Alle verfügbaren Social-Media-Kanäle",
      "3 Beiträge pro Tag je Kanal — bis zu 6× das Volumen von Wachstum",
      "Alles aus Wachstum",
      "Volle Content-Vielfalt: Bilder, Videos & saisonale Aktionen",
      "Bis zu 2 Marken / Standorte",
      "Ersetzt eine Agentur — zum Bruchteil der Kosten",
    ],
    limits: {
      brands: 2,
      channels: null,             // alle verfügbaren Kanäle
      postsPerMonth: null,        // Tages-Schlagzahl × Kanäle, kein Monatsdeckel
      postsPerDayPerChannel: 3,
    },
    featureGates: {
      link_in_bio:        true,
      website_insights:   true,
      simple_assistant:   true,
      bonus_center:       true,
      masterpost:         true,
      content_tools:      true,
      strategy:           true,
      profile_optimizer:  true,
      social_inbox:       true,
      advanced_analytics: true,
      evergreen:          true,
      campaigns:          true,
      media_library:      true,
      multi_brand:        true,   // bis 2 Marken / Standorte inklusive
      advanced_formats:   true,
      team_access:        false,  // ab Enterprise
      client_workspaces:  false,  // ab Enterprise
    },
    cta: "Dominanz starten",
    ctaVariant: "outline",
  },
  {
    id: "agency",
    name: "Enterprise",
    priceMonthly: null,           // bewusst KEIN Festpreis — reine Anfrage-Stufe
    priceYearly: null,
    priceLabel: "Individuell",
    badge: "Enterprise",
    highlighted: false,
    requestOnly: true,            // kein Checkout: CTA führt in den Kontakt-Flow
    requestTo: "/kontakt?anliegen=enterprise-paket",
    promise: "Mehrere Marken, Sonderworkflows, White-Label — dein Zuschnitt",
    description:
      "Für Agenturen, Influencer und Creator-Teams mit mehreren Marken, hohen Volumina oder Sonderautomationen — wir schneiden das Paket auf dich zu.",
    idealFor: ["Agenturen", "Influencer & Creator-Teams", "Mehrere Marken", "White-Label"],
    features: [
      "Mehrere Marken, Standorte oder Kunden",
      "Team-Zugang & getrennte Workspaces",
      "Individuelle Posting-Volumina & Workflows",
      "White-Label & Sonderautomationen",
      "Kunden-Reporting & Export",
      "Persönlicher Ansprechpartner",
    ],
    limits: {
      brands: null,
      channels: null,
      postsPerMonth: null,
      postsPerDayPerChannel: null,
    },
    featureGates: {
      link_in_bio:        true,
      website_insights:   true,
      simple_assistant:   true,
      bonus_center:       true,
      masterpost:         true,
      content_tools:      true,
      strategy:           true,
      profile_optimizer:  true,
      social_inbox:       true,
      advanced_analytics: true,
      evergreen:          true,
      campaigns:          true,
      media_library:      true,
      multi_brand:        true,
      advanced_formats:   true,
      team_access:        true,   // Team-Zugang (mehrere Nutzer) inklusive
      client_workspaces:  true,   // Getrennte Brand-Workspaces pro Kunde inklusive
    },
    cta: "Anfrage senden",
    ctaVariant: "outline",
  },
];

// ── Add-ons ───────────────────────────────────────────────────────────────────
//
// Bereinigt 2026-07-19 mit der 5-Stufen-Logik: „Extra Kanal" (12 €) unterlief die
// 1/3-Kanal-Staffel, „Tägliche Posts" (49 €) kollidierte mit dem Starter-Versprechen,
// „Individuelles Paket" ist jetzt die Enterprise-Karte, „Google Unternehmensprofil"
// wartet auf Google-API-Freischaltung (Antrag läuft) — alle vier entfernt.

// DOC-02 (16.08.2026): Dieses Add-on stand mit festem Monatspreis auf der
// öffentlichen Preisseite — kaufen konnte man es nicht. Die AddonCard rendert nur
// Text (kein onClick, kein Checkout), und im Backend gibt es kein Multi-Brand-Modell:
// „brands" ist ein reiner Anzeigewert in billing/plans.py, es zählt und erzwingt ihn
// niemand. Ein bezifferter Preis für etwas, das weder buchbar noch gebaut ist, ist auf
// einer Seite, auf die bezahlte Werbung zeigt, ein Versprechen zu viel.
// Deshalb: als „auf Anfrage" gekennzeichnet, mit einem Weg, der wirklich irgendwohin
// führt. Der Preis kommt zurück, sobald das Modell existiert.
export const pricingAddons = [
  {
    id: "extra_brand",
    name: "Extra Standort / Extra Brand",
    priceLabel: "auf Anfrage",
    status: "coming_soon",
    icon: "🏢",
    description: "Weiteren Standort oder eine weitere Marke unter eigenen Profil-Einstellungen führen. In Vorbereitung — sprich uns an, wenn du das brauchst, dann melden wir uns, sobald es so weit ist.",
    idealFor: ["Franchise-Betriebe", "Mehrmarken-Unternehmen"],
  },
];

// ── Feste Service-Angebote (Kontakt-Flow, KEIN Checkout) ──────────────────────
// Reguläre, live buchbare Zusatz-Services. Klick → /kontakt mit Vorauswahl (mailto).
export const pricingServices = [
  {
    id: "website-optimierung",
    name: "Website-Optimierung",
    icon: "🌐",
    priceLabel: "ab 79 €/Monat",
    description:
      "KI-gestützte Website-Analyse, Conversion-Optimierung und Inhaltsverbesserung — direkt aus dem Förderfuchs-Cockpit.",
    idealFor: ["Unternehmen mit Website", "E-Commerce", "Dienstleister & Berater"],
    cta: "Beratung anfragen",
    to: "/kontakt?anliegen=website-optimierung&usecase=Unternehmen%20mit%20Website",
  },
  {
    id: "ki-agenten",
    name: "KI-Agentensysteme & Automatisierung",
    icon: "🤖",
    priceLabel: "Preis auf Anfrage",
    description:
      "Wir bauen dein KI-Agentensystem wie bei Förderfuchs und automatisieren individuelle Unternehmensabläufe — vom Konzept bis zum laufenden Betrieb.",
    idealFor: ["Interne Prozessautomatisierung", "Individuelle Workflows", "Wachsende Teams"],
    cta: "Projekt anfragen",
    to: "/kontakt?anliegen=ki-agentensystem&usecase=Interne%20Prozessautomatisierung",
  },
];

// ── Format-Module (Coming Soon) ───────────────────────────────────────────────
//
// Produkt-Bausteine für höherwertige Content-Formate (Karussell, Video/Reel).
// Diese Module sind noch nicht buchbar — status: "coming_soon".
//
// Felder:
//   id               → stabiler Identifier (z.B. "carousel_basic")
//   name             → Anzeigename
//   icon             → Emoji-Icon
//   description      → 1–2 Sätze Produktbeschreibung
//   priceMonthlyRange → Preisrange als String (noch kein Festpreis)
//   status           → "coming_soon" | "beta" | "available"
//   includedIn[]     → Plan-IDs, in denen dieses Modul später inklusive sein soll
//   addonFor[]       → Plan-IDs, für die es als Add-on buchbar sein soll
//   hint             → 1 Satz, wofür das Modul ideal ist (für UI-Tooltip/Badge)
//   releasePriority  → 1 (hoch) bis 3 (niedrig), für spätere Roadmap-Sortierung
//
// Aktivierungslogik:
//   status: "coming_soon" → Karte gedimmt, kein buchbarer CTA, „Demnächst"-Badge
//   status: "beta"        → Karte aktiv, CTA „Beta-Zugang anfragen"
//   status: "available"   → verhält sich wie reguläre Add-on-Karte mit Preis

export const pricingFormatModules = [
  {
    id: "carousel_basic",
    name: "Karussell-Generator",
    icon: "🎠",
    description:
      "KI-generierte Karussell-Posts für Instagram & LinkedIn — Folien-Texte, Titelvorschläge und Layout-Briefings in einem Schritt.",
    priceMonthlyRange: "ab 19 €/Monat",
    status: "coming_soon",
    includedIn: [],            // geplant: ["creator", "scale", "agency"]
    addonFor: ["solo", "business", "creator"],
    hint: "Ideal für Coaches, Bildungsanbieter und alle, die komplexe Themen in verdauliche Schritte aufteilen wollen.",
    releasePriority: 1,
  },
  {
    id: "video_brief",
    name: "Video-Briefing & Reel-Skript",
    icon: "🎬",
    description:
      "Strukturiertes Skript, Hook-Ideen und Briefing für Reels, TikToks und YouTube Shorts — direkt aus dem bestehenden Post-Content generiert.",
    priceMonthlyRange: "ab 29 €/Monat",
    status: "coming_soon",
    includedIn: [],            // geplant: ["scale", "agency"]
    addonFor: ["solo", "business", "creator", "scale", "agency"],
    hint: "Für Creator und Agenturen, die Video-Content skalieren wollen, ohne jedes Mal bei null anzufangen.",
    releasePriority: 1,
  },
];

// ── FAQ ───────────────────────────────────────────────────────────────────────

export const pricingFaq = [
  {
    q: "Kann ich jederzeit den Plan wechseln?",
    a: "Ja. Du kannst jederzeit upgraden oder downgraden. Änderungen werden zum nächsten Abrechnungszeitraum wirksam.",
  },
  {
    q: "Was passiert nach der Testphase?",
    a: "Du kannst Förderfuchs 14 Tage lang kostenlos und ohne Kreditkarte testen — mit allen Funktionen. Veröffentlicht wird in der Testphase über Bluesky; mit einem gekauften Paket kommt LinkedIn dazu. Facebook, Instagram, Threads und das Google Unternehmensprofil sind in der Freigabe durch den jeweiligen Anbieter und kommen ohne Aufpreis dazu, sobald sie offen sind. Danach wählst du aktiv deinen Plan — keine automatische Abbuchung.",
  },
  {
    q: "Was bedeutet 'Kanal'?",
    a: "Ein Kanal ist ein verbundenes Social-Media-Profil. Heute direkt verbindbar: Bluesky und LinkedIn. Facebook, Instagram, Threads und das Google Unternehmensprofil sind in der Freischaltung und folgen mit deinem Paket; TikTok ist in Vorbereitung. In der Testphase veröffentlichst du über Bluesky.",
  },
  {
    q: "Wie viele Beiträge bekomme ich in meinem Paket?",
    a: "Jedes Paket hat eine feste tägliche Schlagzahl je Kanal: Starter = 1 Beitrag pro Tag auf 1 Kanal. Wachstum = je 1 Beitrag pro Tag auf 3 Kanälen (bis zu 90 Veröffentlichungen im Monat). Dominanz = je 3 Beiträge pro Tag auf allen verfügbaren Kanälen. Jede Veröffentlichung auf einem Kanal zählt dabei als 1 Beitrag.",
  },
  {
    q: "Warum kostet Dominanz 699 €?",
    a: "Dominanz liefert bis zum Sechsfachen des Wachstum-Volumens: alle verfügbaren Kanäle, je 3 Beiträge täglich. Linear weitergerechnet wären das über 1.500 € im Monat — als Paket kostet es 699 €. Zum Vergleich: Agenturen berechnen für täglichen Multi-Kanal-Output mehrere tausend Euro monatlich. Der Preis ist bewusst premium — und dabei ein Bruchteil einer Agentur.",
  },
  {
    q: "Was, wenn ich mehr brauche — mehrere Marken, Teams oder Sonderworkflows?",
    a: "Dafür gibt es Enterprise: individuelle Pakete für Agenturen, Influencer, Creator-Teams, mehrere Marken, White-Label oder Sonderautomationen. Kein Festpreis — wir schneiden Umfang und Preis auf deinen Bedarf zu. Eine kurze Anfrage über die Kontaktseite genügt. Einzelne weitere Marken/Standorte als Add-on sind in Vorbereitung (aktuell auf Anfrage) — sprich uns an, wenn du das brauchst.",
  },
  {
    q: "Wie funktionieren Add-ons?",
    a: "Add-ons werden monatlich zum gewählten Plan addiert. Du kannst sie jederzeit hinzufügen oder wieder entfernen.",
  },
  {
    q: "Gibt es eine jährliche Zahlungsoption?",
    a: yearlyDiscountInfo.faqNote,
  },
  {
    q: "Ist mein Abonnement monatlich kündbar?",
    a: "Ja, bei monatlicher Zahlung kannst du jederzeit zum Monatsende kündigen. Beim Jahresabo gilt die Mindestlaufzeit von 12 Monaten.",
  },
];

// ── Zielgruppen-Empfehlungen ──────────────────────────────────────────────────

/**
 * Mapping von Audience-Keys zu Plan-Empfehlungen.
 * Keys entsprechen den Audience-Typen im Onboarding / Quiz.
 *
 * Felder:
 *   label          → Anzeigename im UI
 *   primaryPlan    → empfohlener Plan (plan.id)
 *   secondaryPlan  → Alternativplan (plan.id)
 *   hint           → kurzer Badge-Text auf der Plankarte
 *   note           → erläuternder Hinweis (1 Satz)
 *   suggestedAddons → Array von addon.id-Empfehlungen
 */
export const audienceRecommendations = {
  local_business: {
    label: "Lokales Unternehmen",
    primaryPlan: "business",
    secondaryPlan: "creator",
    hint: "Gut für lokale Unternehmen",
    note: "Typische Wahl: Starter — dein wichtigster Kanal läuft täglich. Mit Wachstum wirst du auf drei Kanälen gleichzeitig sichtbar.",
    suggestedAddons: ["extra_brand"],
  },
  coach_consultant: {
    label: "Coach / Berater",
    primaryPlan: "business",
    secondaryPlan: "creator",
    hint: "Beliebt bei Coaches",
    note: "Viele Coaches starten mit Starter und wechseln auf Wachstum, sobald der Content-Bedarf steigt.",
    suggestedAddons: [],
  },
  creator: {
    label: "Creator / Influencer",
    primaryPlan: "creator",
    secondaryPlan: "scale",
    hint: "Ideal für Creator",
    note: "Drei Kanäle täglich automatisch — Wachstum ist die erste Wahl. Für maximale Schlagzahl auf allen Kanälen gibt es Dominanz.",
    suggestedAddons: [],
  },
  agency: {
    label: "Agentur",
    primaryPlan: "agency",
    secondaryPlan: "scale",
    hint: "Gemacht für Agenturen",
    note: "Mehrere Kunden, Team-Zugang, White-Label — als Enterprise-Paket individuell zugeschnitten. Eine kurze Anfrage genügt.",
    suggestedAddons: ["extra_brand"],
  },
  education_provider: {
    label: "Bildungsanbieter",
    primaryPlan: "creator",
    secondaryPlan: "business",
    hint: "Gut für Bildungsanbieter",
    note: "Regelmäßige Inhalte auf drei Kanälen — Wachstum deckt das mit Strategie und Analytics ab.",
    suggestedAddons: [],
  },
};

// ── Helper-Funktionen ─────────────────────────────────────────────────────────

/**
 * Gibt die Zielgruppen-Empfehlung für einen Audience-Key zurück.
 * Pure Funktion — kein State, kein Async.
 *
 * @param {string} audienceKey — z.B. "local_business", "creator", "agency"
 * @returns {object|null} Empfehlungs-Objekt oder null wenn Key unbekannt
 */
export function recommendPlanForAudience(audienceKey) {
  return audienceRecommendations[audienceKey] ?? null;
}

/**
 * Gibt einen Plan per ID zurück.
 * @param {string} planId — z.B. "solo", "business", "creator", "scale", "agency"
 * @returns {object|null}
 */
export function getPlanById(planId) {
  return pricingPlans.find((p) => p.id === planId) ?? null;
}

/**
 * Gibt ein Add-on per ID zurück.
 * @param {string} addonId — z.B. "extra_brand"
 * @returns {object|null}
 */
export function getAddonById(addonId) {
  return pricingAddons.find((a) => a.id === addonId) ?? null;
}

/**
 * Gibt ein Format-Modul per ID zurück.
 * Pure Funktion — kein State, kein Async.
 *
 * @param {string} moduleId — z.B. "carousel_basic", "video_brief"
 * @returns {object|null}
 */
export function getFormatModuleById(moduleId) {
  return pricingFormatModules.find((m) => m.id === moduleId) ?? null;
}

/**
 * Gibt den ersten Plan zurück, in dem ein Feature freigeschaltet ist.
 * Durchsucht pricingPlans in ihrer definierten Reihenfolge (solo → agency).
 * Pure Funktion — kein State, kein Async, niemals Exceptions nach außen.
 *
 * Verwendung:
 *   const plan = getRequiredPlanForFeature("evergreen");
 *   // → { id: "creator", name: "Wachstum", ... }
 *
 * @param {string} featureKey — z.B. "evergreen", "content_tools", "team_access"
 * @returns {object|null} Plan-Objekt oder null wenn kein Plan das Feature freischaltet
 */
export function getRequiredPlanForFeature(featureKey) {
  return pricingPlans.find((p) => p.featureGates?.[featureKey] === true) ?? null;
}
